import HashtagsContainer from "@components/HashtagsContainer/Hashtags.container";
import {
    flexCenter,
    flexColumn,
    marginToChildren,
    transitionMixin,
} from "@components/mixins";
import ResponsiveImage from "@components/ResponsiveImage/ResponsiveImg.component";
import SpinnerComponent from "@components/Spinner/Spinner.component";
import { useAnimationLayout } from "@utils/animationUtils";
import { getAllSizesPNG } from "@utils/helpers";
import { useFetch } from "@utils/hooks";
import { useRef } from "react";
import { Link } from "react-router-dom";
import styled, { css } from "styled-components";
import { animationLayout } from "./animationLayout";

const ArticlePreviewWrapper = styled.div`
    ${flexCenter};
    ${flexColumn};

    @media screen and (max-width: 560px) {
        padding: 0 30px;
    }

    @media screen and (max-width: 400px) {
        padding: 0 20px;
    }
`;

const PreviewWrapper = styled.div`
    ${flexCenter};
    ${flexColumn};
    width: 100%;
    padding: 0 15px;

    ${({ typeOfAlignment }) =>
        typeOfAlignment === "row"
            ? css`
                  flex-direction: row;
              `
            : ""}
`;

const PreviewContainer = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-around;
    width: 100%;
    height: auto;
    margin: 30px 15px;

    a {
        width: 100%;
    }

    ${({ inverted }) =>
        inverted
            ? css`
                  flex-direction: row-reverse;
              `
            : ""}

    ${({ typeOfAlignment }) =>
        typeOfAlignment === "column"
            ? css`
                  flex-direction: column;
                  justify-content: flex-end;
              `
            : ""}

    @media screen and (max-width: 1050px) {
        ${flexColumn};
        ${flexCenter};
    }

    @media screen and (max-width: 560px) {
        margin: 15px 0;
        height: 100%;
        max-height: unset;
        min-height: unset;
    }
`;

const ImageContainer = styled.div`
    width: 620px;
    margin: 0 20px;
    overflow: hidden;

    img {
        ${flexCenter};
        width: 100%;
        max-width: calc(100% - 30px);
        max-height: 450px;
        object-fit: cover;
        ${transitionMixin("1s")};
    }

    &:hover {
        img {
            transform: scale(1.03);
        }
    }

    @media screen and (max-width: 1250px) {
        width: 420px;
    }

    @media screen and (max-width: 1050px) {
        margin: 20px auto;
        width: 100%;
        max-width: 100%;
        height: auto;
        max-height: 400px;

        img {
            width: 100%;
        }
    }

    @media screen and (max-width: 830px) {
        margin: 0 auto;

        img {
            margin: 0 auto;
            height: auto;
        }
    }

    @media screen and (max-width: 560px) {
        height: 100%;

        img {
            height: 100%;
            max-width: 100%;
            max-height: 320px;
        }
    }
`;

export const ContentContainer = styled.div`
    ${flexColumn};
    justify-content: flex-start;
    align-items: flex-start;
    width: 100%;

    p {
        font-size: 1.5rem;
        color: ${({ theme }) => theme.colors.dark_grey};
        font-family: ${({ theme }) => theme.ff.Cormorant};
        line-height: 160%;
    }

    @media screen and (max-width: 830px) {
        ${flexCenter};
        text-align: center;

        p {
            width: 75%;
        }
    }

    @media screen and (max-width: 560px) {
        text-align: center;

        p {
            width: 100%;
        }
    }
`;

const Title = styled.h4`
    text-transform: uppercase;
    padding-bottom: 15px;
    overflow: hidden;

    @-moz-document url-prefix() {
        position: relative;
    }

    a {
        position: relative;
        text-decoration: none;
        color: inherit;

        @-moz-document url-prefix() {
            position: static;
        }

        &::before {
            content: "";
            position: absolute;
            bottom: -5px;
            left: 0;
            width: 100%;
            height: 2px;
            background-color: ${({ theme }) => theme.colors.black};
            transform: translateX(-150%);
            ${transitionMixin("0.4s")};

            @-moz-document url-prefix() {
                bottom: 10px;
            }
        }

        &:hover {
            &::before {
                transform: translateX(0);
            }
        }
    }

    @media screen and (max-width: 830px) {
        margin: 10px 0;
    }

    @media screen and (max-width: 560px) {
        padding: 0 5px;
    }
`;

const PreviewContainerSocial = styled.div`
    ${flexCenter};
    justify-content: flex-start;
    align-items: flex-start;
    margin: 30px auto;

    ${PreviewContainer} {
        margin: 0;
        max-width: 70%;

        ${ImageContainer} {
            margin: 0 0 20px;
        }
    }

    ${({ inverted }) =>
        inverted
            ? css`
                  flex-direction: row-reverse;
              `
            : ""}

    a {
        width: 100%;
    }

    ${ImageContainer} {
        justify-self: flex-start;
        width: 100%;
        margin: 20px 0;

        img {
            width: 100%;
            max-width: 100%;
        }
    }

    @media screen and (max-width: 830px) {
        ${ContentContainer} {
            text-align: center;

            p {
                width: 100%;
            }
        }
    }

    @media screen and (max-width: 560px) {
        ${flexColumn};
        width: 100%;

        ${PreviewContainer} {
            margin: 10px auto 20px;
            max-width: 100%;
        }
    }
`;

export const SocialBox = styled.div`
    ${flexCenter};
    align-items: flex-start;
    justify-content: flex-start;
    min-width: 400px;
    margin: 0 15px;
    padding: 15px;
    text-align: center;
    ${marginToChildren()};
    background-color: ${({ theme }) => theme.colors.light_grey};

    .left {
        ${flexColumn};
        width: 100%;
        height: 100%;
        padding-left: 20px;

        h2.title {
            display: inline-flex;
            font-weight: ${({ theme }) => theme.fw.black};
            text-transform: uppercase;
            margin-bottom: 15px;
        }
    }

    .right {
        ${flexColumn};
        width: 100%;
        height: 100%;

        p.social-title {
            margin: 5px 0 30px;
            font-family: ${({ theme }) => theme.ff.Cormorant};
            font-size: ${({ theme }) => theme.fz.M};
            line-height: 140%;
        }
    }

    p {
        &.join {
            text-align: left;
            margin: 5px 0;

            &:last-of-type {
                margin: 5px 0 0;
            }
        }

        b {
            font-weight: ${({ theme }) => theme.fw.semiBold};
        }

        &.social-score {
            margin-top: 30px;
            font-size: ${({ theme }) => theme.fz.L};
            animation: 0.5s scaling infinite alternate;

            @keyframes scaling {
                from {
                    transform: scale(1);
                }
                to {
                    transform: scale(1.2);
                }
            }
        }
    }

    a {
        margin: 0 10px;
        font-size: ${({ theme }) => theme.fz.XL};
        color: ${({ theme }) => theme.colors.black};
        ${transitionMixin()};

        &:hover {
            color: ${({ theme }) => theme.colors.grey};
        }
    }

    @media screen and (max-width: 1050px) {
        min-width: 350px;
    }

    @media screen and (max-width: 560px) {
        margin: 0 auto;
        min-width: 100%;

        .left {
            p {
                font-size: ${({ theme }) => theme.fz.XXS};
            }

            h2.title {
                font-size: ${({ theme }) => theme.fz.M};
            }
        }

        .right {
            a {
                font-size: ${({ theme }) => theme.fz.M};
            }

            p {
                &.social-score {
                    font-size: ${({ theme }) => theme.fz.S};
                }

                &.social-title {
                    font-size: ${({ theme }) => theme.fz.XS};
                }
            }
        }
    }
`;

const ArticlePreview = ({
    inverted,
    slug,
    title,
    lead,
    imgSet,
    socialVariant,
}) => {
    const { small, medium, large } = getAllSizesPNG(imgSet);

    const containerRef = useRef();

    useAnimationLayout(containerRef, animationLayout);

    const BasicTemplate = () => (
        <PreviewWrapper ref={containerRef}>
            <PreviewContainer inverted={inverted}>
                <Link to={`/artykuly/${slug}`}>
                    <ImageContainer>
                        <ResponsiveImage
                            small={small}
                            medium={medium}
                            large={large}
                            alt={title}
                        />
                    </ImageContainer>
                </Link>
                <ContentContainer>
                    <Title>
                        <Link to={`/artykuly/${slug}`}>{title}</Link>
                    </Title>
                    <p>{lead}</p>
                    <HashtagsContainer hashtags={["wyprawka", "dziecko"]} />
                </ContentContainer>
            </PreviewContainer>
        </PreviewWrapper>
    );

    const TemplateWithSocial = () => (
        <PreviewWrapper ref={containerRef}>
            <PreviewContainerSocial inverted={inverted}>
                <PreviewContainer typeOfAlignment="column">
                    <Link to={`/artykuly/${slug}`}>
                        <ImageContainer>
                            <ResponsiveImage
                                small={small}
                                medium={medium}
                                large={large}
                                alt={title}
                            />
                        </ImageContainer>
                    </Link>
                    <ContentContainer>
                        <Title>
                            <Link to={`/artykuly/${slug}`}>{title}</Link>
                        </Title>
                        <p>{lead}</p>
                        <HashtagsContainer hashtags={["wyprawka", "dziecko"]} />
                    </ContentContainer>
                </PreviewContainer>
                <SocialBox>
                    <div className="left">
                        <h2 className="title">
                            Dołącz
                            <br />
                            do nas
                        </h2>
                        <p className="join">Konkursy 💛</p>
                        <p className="join">Rozdania nagród 🎁</p>
                        <p className="join">Newsy 😊</p>
                    </div>
                    <div className="right">
                        <p className="social-title">
                            na kanałach społecznościowych
                        </p>

                        <div>
                            <a href="https://www.facebook.com/Zdrowe-Dziecko-Magazyn-dla-rodzic%C3%B3w-104668118492222">
                                <i className="fab fa-facebook-f"></i>
                            </a>
                            <a href="https://www.instagram.com/magazyn_zdrowe_dziecko/">
                                <i className="fab fa-instagram"></i>
                            </a>
                        </div>
                        <div>
                            <p className="social-score">
                                <b>8,2 k</b>
                            </p>
                        </div>
                    </div>
                </SocialBox>
            </PreviewContainerSocial>
        </PreviewWrapper>
    );

    return socialVariant ? <TemplateWithSocial /> : <BasicTemplate />;
};

export const ArticlesPreviewTop = (props) => {
    const { data, loading, error } = useFetch(
        "https://admin.magazynzdrowedziecko.pl/articles?_sort=position:DESC"
    );

    const articleElements = data
        ?.slice(0, 4)
        .map(({ Title: title, Lead: lead, Image: imgSet, slug, id }) => (
            <ArticlePreview
                key={id}
                title={title}
                lead={lead}
                imgSet={imgSet}
                slug={slug}
                inverted={id % 2 === 0}
                socialVariant
            />
        ));

    if (error) {
        return (
            <ArticlePreviewWrapper>
                <h2>Coś poszło nie tak. Spróbuj ponownie później.</h2>
            </ArticlePreviewWrapper>
        );
    }

    return (
        <ArticlePreviewWrapper>
            {loading ? <SpinnerComponent /> : articleElements}
        </ArticlePreviewWrapper>
    );
};

const ArticlesPreviewBottom = (props) => {
    const { data, loading, error } = useFetch(
        "https://admin.magazynzdrowedziecko.pl/articles?_sort=position:DESC"
    );

    const articleElements = data
        ?.slice(4, 11)
        .map(({ Title: title, Lead: lead, Image: imgSet, slug, id }) => (
            <ArticlePreview
                key={id}
                title={title}
                lead={lead}
                imgSet={imgSet}
                slug={slug}
                inverted={id % 2 === 0}
            />
        ));

    if (error) {
        return (
            <ArticlePreviewWrapper>
                <h2>Coś poszło nie tak. Spróbuj ponownie później.</h2>
            </ArticlePreviewWrapper>
        );
    }

    return (
        <ArticlePreviewWrapper>
            {loading ? <SpinnerComponent /> : articleElements}
        </ArticlePreviewWrapper>
    );
};
export default ArticlesPreviewBottom;

/* jpg : https://www.getcreative.edu.pl/template/g/images/nowa-era-banner.jpg */
